import React, { useState } from "react";
import { Menu, X } from "lucide-react";

const VIDEOS = [
  {
    url: "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260702_081127_0992a171-d3c6-4978-8213-0ec5df8b6d63.mp4",
    label: "Golden Hour",
  },
];

const OVERLAY_IMG = "https://soft-zoom-63098134.figma.site/_assets/v11/0b4a435b2df2747593c43d7a1c9b4578f7d8d90c.png";

const NAV_LINKS = [
  { label: "About", href: "#about" },
  { label: "Projects", href: "#projects" },
  { label: "Contact", href: "#contact" },
];

export default function Hero() {
  const [activeVideo, setActiveVideo] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const textColor = "#ffffff";

  const scrollTo = (href) => {
    setMobileMenuOpen(false);
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full h-screen overflow-hidden bg-black"
    >
      {/* ===== VIDEO LAYER (z-0) ===== */}
      {VIDEOS.map((v, i) => (
        <video
          key={i}
          src={v.url}
          autoPlay
          muted
          loop
          playsInline
          className={`absolute inset-0 w-full h-full object-cover video-crossfade ${
            i === activeVideo ? "opacity-100" : "opacity-0"
          }`}
          style={{ zIndex: 0 }}
          data-testid={`hero-video-${i}`}
        />
      ))}

      {/* ===== TRANSPARENT PNG OVERLAY (z-1) ===== */}
      <div
        className="absolute inset-0 train-bob pointer-events-none"
        style={{ zIndex: 1 }}
        aria-hidden="true"
      >
        <img
          src={OVERLAY_IMG}
          alt=""
          className="w-full h-full object-cover"
          role="presentation"
        />
      </div>

      {/* ===== CONTENT LAYER (z-2) ===== */}
      <div
        className="relative flex flex-col h-full px-5 sm:px-8 md:px-12 lg:px-16 py-5 sm:py-6"
        style={{ zIndex: 2 }}
      >
        {/* ===== NAVIGATION ===== */}
        <nav className="flex items-center justify-between" data-testid="hero-nav">
          {/* Logo */}
          <a
            href="#home"
            className="text-white text-xl sm:text-2xl italic"
            data-testid="hero-logo"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            Sean Fee
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center liquid-glass rounded-full px-2 py-1.5">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="text-white/90 text-sm px-4 py-2 hover:text-white transition-colors"
                style={{ fontFamily: "system-ui, sans-serif" }}
                data-testid={`hero-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contact")}
              className="bg-white text-black text-sm font-medium px-5 py-2 rounded-full hover:bg-white/90 transition-colors ml-1"
              style={{ fontFamily: "system-ui, sans-serif" }}
              data-testid="hero-nav-cta"
            >
              Get In Touch
            </button>
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden liquid-glass rounded-full w-10 h-10 flex items-center justify-center"
            data-testid="hero-mobile-menu-btn"
            aria-label="Toggle menu"
          >
            <div className="relative w-5 h-5">
              <Menu
                className={`absolute inset-0 w-5 h-5 text-white ${
                  mobileMenuOpen ? "menu-icon-exit" : "menu-icon-active"
                }`}
              />
              <X
                className={`absolute inset-0 w-5 h-5 text-white ${
                  mobileMenuOpen ? "menu-icon-active" : "menu-icon-enter"
                }`}
              />
            </div>
          </button>
        </nav>

        {/* ===== MOBILE MENU OVERLAY ===== */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center gap-8"
            data-testid="mobile-menu-overlay"
          >
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-5 right-5 liquid-glass rounded-full w-10 h-10 flex items-center justify-center"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            {NAV_LINKS.map((link, i) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                className="text-white text-3xl"
                style={{
                  fontFamily: "'Instrument Serif', serif",
                  animation: `fadeInUp 500ms cubic-bezier(0.4,0,0.2,1) ${100 + i * 50}ms both`,
                }}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollTo("#contact")}
              className="bg-white text-black text-lg font-medium px-8 py-3 rounded-full mt-4"
              style={{
                fontFamily: "system-ui, sans-serif",
                animation: "fadeInUp 500ms cubic-bezier(0.4,0,0.2,1) 300ms both",
              }}
            >
              Get In Touch
            </button>
          </div>
        )}

        {/* ===== HERO CONTENT (centered) ===== */}
        <div className="flex-1 flex flex-col items-center justify-center text-center max-w-4xl mx-auto -mt-8">
          {/* Heading */}
          <h1
            className="text-4xl sm:text-5xl md:text-7xl lg:text-[5.5rem] leading-[1.1] mb-6"
            style={{ color: textColor }}
            data-testid="hero-heading"
          >
            Founder. Builder.
            <br />
            Serial optimist.
          </h1>

          {/* Subtext */}
          <p
            className="max-w-xl leading-relaxed mb-10 text-sm sm:text-base"
            style={{ fontFamily: "system-ui, sans-serif", color: "rgba(255,255,255,0.7)" }}
            data-testid="hero-subtitle"
          >
            Entrepreneur, advisor &amp; investor. Building for creativity and community.
          </p>

          {/* CTA Button */}
          <button
            onClick={() => scrollTo("#projects")}
            className="liquid-glass rounded-full px-8 py-3.5 text-sm font-medium text-white hover:bg-white/10 transition-colors"
            style={{ fontFamily: "system-ui, sans-serif" }}
            data-testid="hero-cta-btn"
          >
            Explore Portfolio
          </button>
        </div>

        {/* ===== BOTTOM STATS ===== */}
        <div
          className="flex flex-wrap items-center justify-center gap-3 sm:gap-0 text-white/70 text-xs sm:text-sm pb-2"
          style={{ fontFamily: "system-ui, sans-serif" }}
          data-testid="hero-stats"
        >
          <span>15+ Years Building</span>
          <span className="hidden sm:inline mx-4 text-white/30">|</span>
          <span>11+ Companies</span>
          <span className="hidden sm:inline mx-4 text-white/30">|</span>
          <span>3 Acquired</span>
          <span className="hidden sm:inline mx-4 text-white/30">|</span>
          <span>Creativity, Community and Craft</span>
        </div>
      </div>

      {/* Inline animation keyframe for mobile menu */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
